import * as React from "react";
import { CustomQuestionOptionEditor, QuestionOptionEditorDraft } from "./CustomQuestionOptionEditor";
import { SurveyQuestionType, assertNever } from "../../../../../../../amorg-common";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { RadioButtonButtonList } from "../../../../../../../designSystem/inputs/RadioButtonButton";
import { Label } from "../../../../../../../designSystem/Label";

export type QuestionConfigEditorDraft = {
    type:'default',
} |
{
    type:'custom',
    optionConfig:QuestionOptionEditorDraft,
} |
{
    type:'multiple-choice',
    optionConfig:QuestionOptionEditorDraft,
}

type QuestionConfigEditorProps = {
    disabled?: boolean,
    disableTypeChange?: boolean,
    draft: QuestionConfigEditorDraft,
    onChange: (config: QuestionConfigEditorDraft) => void,
};

export function QuestionConfigEditor(props: QuestionConfigEditorProps): JSX.Element
{
    function handleTypeChange(nextType:SurveyQuestionType)
    {
        if (props.disabled || props.disableTypeChange)
        {
            return;
        }

        if (nextType === "default")
        {
            return props.onChange({ type: "default" });
        }
        else if(nextType === "custom")
        {
            return props.onChange({
                type: "custom",
                optionConfig: {
                    scored: true,
                    options: [
                        { text: "Meget uenig", value: "1", score: 1, originalIndex: null },
                        { text: "Uenig", value: "2", score: 2, originalIndex: null },
                        { text: "Neutral", value: "3", score: 3, originalIndex: null },
                        { text: "Enig", value: "4", score: 4, originalIndex: null },
                        { text: "Meget enig", value: "5", score: 5, originalIndex: null }
                    ]
                }
            });
        }
        else if(nextType === "multiple-choice")
        {
            return props.onChange({
                type: "multiple-choice",
                optionConfig: {
                    scored: false,
                    options: [
                        { text: "Svarmulighed 1", value: "option_1", originalIndex: null },
                        { text: "Svarmulighed 2", value: "option_2", originalIndex: null },
                    ]
                }
            });
        }
        else {
            assertNever(nextType);
        }
    }

    return (
        <Flex flexDirection="column" gap={8}>
            <Label>Spørgsmålstype</Label>
            <div style={{ border: "1px solid #d0d5dd", borderRadius: 8, padding: 12, backgroundColor: "#f8f9fb" }}>
                <RadioButtonButtonList
                    groupName="questionType"
                    disabled={props.disabled || props.disableTypeChange}
                    options={[
                        { text: "Standard", value: "default" },
                        { text: "Tilpasset", value: "custom" },
                        { text: "Multiple choice", value: "multiple-choice" }
                    ]}
                    selectedValue={props.draft.type}
                    onChange={(value) => handleTypeChange(value as SurveyQuestionType)}
                />
                <div style={{ marginTop: 12 }}>
                    
                    {props.draft.type === "default" &&
                        <Flex flexDirection="column" gap={4}>
                            <Label>Standard svarskala</Label>
                            <div style={{ color: "#475467", fontSize: 14 }}>
                                Standardspørgsmål bruger en 5-punkts Likert-skala med disse værdier:
                                <ul style={{ margin: "4px 0 0 20px", padding: 0 }}>
                                    <li>1 – Meget uenig</li>
                                    <li>2 – Uenig</li>
                                    <li>3 – Neutral</li>
                                    <li>4 – Enig</li>
                                    <li>5 – Meget enig</li>
                                </ul>
                            </div>
                        </Flex>
                    }

                    {props.draft.type === "custom" &&
                        <CustomQuestionOptionEditor 
                            disabled={props.disabled} 
                            draft={props.draft.optionConfig} 
                            onChange={(newOptions) =>
                                props.onChange({
                                    type: "custom",
                                    optionConfig: newOptions
                                } satisfies QuestionConfigEditorDraft)
                            }
                        />
                    }

                    {props.draft.type === "multiple-choice" &&
                        <CustomQuestionOptionEditor 
                            hideScoringToggle
                            disabled={props.disabled} 
                            draft={{ scored: false, options: props.draft.optionConfig.options.map(o => ({ originalIndex: o.originalIndex, text: o.text, value: o.value })) }} 
                            onChange={(newOptions) =>
                                props.onChange({
                                    type: "multiple-choice",
                                    optionConfig: { scored: false, options: newOptions.options }
                                } satisfies QuestionConfigEditorDraft)
                            }
                        />
                    }
                    
                </div>
            </div>
        </Flex>
    );
}
